/* eslint-disable max-classes-per-file */
// Both TabCoordinator and SSEService are tightly coupled and belong together

import { sse } from '@/endpoints/notifications';
import { timeLocalFormat } from '@/helpers/dateTime';

/**
 * TabCoordinator - Manages tab coordination using BroadcastChannel
 * Implements leader election pattern for single SSE connection per browser
 *
 * Vue 3 Compatible: Framework-agnostic vanilla JS implementation
 */
class TabCoordinator {
  constructor(channelName) {
    this.channelName = channelName;
    this.channel = new BroadcastChannel(channelName);
    this.isLeader = false;
    this.tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    this.leaderHeartbeatInterval = null;
    this.leaderCheckInterval = null;
    this.lastLeaderHeartbeat = Date.now();
    this.hasSeenLeader = false;
    this.currentElectionId = null;
    this.receivedLeaderResponseDuringElection = false;

    // Callback arrays for event handling (Vue 2/3 compatible pattern)
    this.onMessageCallbacks = [];
    this.onLeaderChangeCallbacks = [];

    this.setupMessageHandlers();
    this.electLeader();
  }

  /**
   * Setup BroadcastChannel message handlers
   */
  setupMessageHandlers() {
    this.channel.onmessage = (event) => {
      const { type, tabId, timestamp, data } = event.data;

      switch (type) {
        case 'LEADER_ELECTION':
          // Another tab is trying to become leader
          if (this.isLeader && tabId !== this.tabId) {
            // We're already the leader, announce it
            this.announceLeadership();
          }
          break;

        case 'LEADER_ANNOUNCEMENT':
          // Another tab announced it's the leader
          if (tabId !== this.tabId) {
            this.hasSeenLeader = true;
            this.receivedLeaderResponseDuringElection = true;
            this.lastLeaderHeartbeat = timestamp;
            if (this.isLeader) {
              // Another leader exists, resolve conflict
              if (tabId > this.tabId) {
                this.resignLeadership();
              } else {
                this.announceLeadership();
              }
            }
          }
          break;

        case 'LEADER_HEARTBEAT':
          // Leader is alive
          if (tabId !== this.tabId) {
            this.lastLeaderHeartbeat = timestamp;
            this.hasSeenLeader = true;
            this.receivedLeaderResponseDuringElection = true;
            if (this.isLeader) {
              // Another leader is sending heartbeats, resolve conflict
              if (tabId > this.tabId) {
                this.resignLeadership();
              } else {
                this.announceLeadership();
              }
            }
          }
          break;

        case 'SSE_EVENT':
          // SSE message from leader, forward to callbacks
          this.onMessageCallbacks.forEach((callback) => {
            try {
              callback(data);
            } catch (error) {
              console.error('TabCoordinator: Error in message callback:', error);
            }
          });
          break;

        default:
          console.warn(`TabCoordinator: Unknown message type: ${type}`);
      }
    };
  }

  /**
   * Initiate leader election process
   */
  electLeader() {
    // Track if we receive a response during this specific election
    const electionId = Date.now();
    this.currentElectionId = electionId;
    this.receivedLeaderResponseDuringElection = false;

    // Announce candidacy
    this.channel.postMessage({
      type: 'LEADER_ELECTION',
      tabId: this.tabId,
      timestamp: Date.now()
    });

    // Wait 100ms to see if there's an existing leader
    setTimeout(() => {
      // Only process if this is still the current election
      if (this.currentElectionId !== electionId) {
        return;
      }

      if (!this.receivedLeaderResponseDuringElection) {
        // No leader responded during this election window - become leader
        this.becomeLeader();
      } else {
        this.startLeaderCheck();
      }
    }, 100);
  }

  /**
   * Become the leader tab
   */
  becomeLeader() {
    this.isLeader = true;
    this.announceLeadership();
    this.startLeaderHeartbeat();
    this.notifyLeaderChange();
  }

  /**
   * Announce leadership to other tabs
   */
  announceLeadership() {
    this.channel.postMessage({
      type: 'LEADER_ANNOUNCEMENT',
      tabId: this.tabId,
      timestamp: Date.now()
    });
  }

  /**
   * Resign from leadership
   */
  resignLeadership() {
    this.isLeader = false;
    this.stopLeaderHeartbeat();
    this.startLeaderCheck();
    this.notifyLeaderChange();
  }

  /**
   * Start sending heartbeat as leader
   */
  startLeaderHeartbeat() {
    this.stopLeaderHeartbeat(); // Clear any existing interval

    this.leaderHeartbeatInterval = setInterval(() => {
      this.channel.postMessage({
        type: 'LEADER_HEARTBEAT',
        tabId: this.tabId,
        timestamp: Date.now()
      });
    }, 2000); // Every 2 seconds
  }

  /**
   * Stop sending heartbeat
   */
  stopLeaderHeartbeat() {
    if (this.leaderHeartbeatInterval) {
      clearInterval(this.leaderHeartbeatInterval);
      this.leaderHeartbeatInterval = null;
    }
  }

  /**
   * Start checking for leader heartbeat (follower tabs)
   */
  startLeaderCheck() {
    this.stopLeaderCheck(); // Clear any existing interval

    this.leaderCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastHeartbeat = now - this.lastLeaderHeartbeat;

      if (timeSinceLastHeartbeat > 5000) {
        // Leader seems dead, initiate new election
        this.stopLeaderCheck();
        this.hasSeenLeader = false;
        this.electLeader();
      }
    }, 3000); // Check every 3 seconds
  }

  /**
   * Stop checking for leader heartbeat
   */
  stopLeaderCheck() {
    if (this.leaderCheckInterval) {
      clearInterval(this.leaderCheckInterval);
      this.leaderCheckInterval = null;
    }
  }

  /**
   * Broadcast a message to all tabs
   * @param {string} type - Message type
   * @param {*} data - Message data
   */
  broadcastMessage(type, data) {
    this.channel.postMessage({
      type,
      tabId: this.tabId,
      timestamp: Date.now(),
      data
    });
  }

  /**
   * Register callback for messages from other tabs
   * @param {Function} callback - Callback function
   */
  onMessage(callback) {
    if (typeof callback === 'function') {
      this.onMessageCallbacks.push(callback);
    }
  }

  /**
   * Register callback for leader change events
   * @param {Function} callback - Callback function with isLeader boolean param
   */
  onLeaderChange(callback) {
    if (typeof callback === 'function') {
      this.onLeaderChangeCallbacks.push(callback);
    }
  }

  /**
   * Notify all callbacks about leader change
   */
  notifyLeaderChange() {
    this.onLeaderChangeCallbacks.forEach((callback) => {
      try {
        callback(this.isLeader);
      } catch (error) {
        console.error('TabCoordinator: Error in leader change callback:', error);
      }
    });
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.stopLeaderHeartbeat();
    this.stopLeaderCheck();

    if (this.channel) {
      this.channel.close();
    }

    this.onMessageCallbacks = [];
    this.onLeaderChangeCallbacks = [];
  }
}

class SSEService {
  constructor() {
    this.eventSource = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.store = null;
    this.i18n = null;
    this.authenticationFailed = false;
    this.reconnectTimeoutId = null;

    // Tab coordination (Vue 2/3 compatible - uses vanilla JS)
    this.tabCoordinator = null;
    this.isLeaderTab = false;
    this.isConnecting = false;
  }

  /**
   * Initialize SSE service with Vue store and i18n instances
   * @param {Object} store - Vuex/Pinia store instance (Vue 2/3 compatible)
   * @param {Object} i18n - Vue i18n instance (Vue 2/3 compatible)
   */
  init(store, i18n) {
    // Prevent double initialization
    if (this.store) {
      console.warn('SSE: Already initialized');
      return;
    }

    this.store = store;
    this.i18n = i18n;

    this.setupTabCoordinator();
  }

  /**
   * Setup TabCoordinator for multi-tab support
   */
  setupTabCoordinator() {
    if (this.tabCoordinator) return;

    // Initialize TabCoordinator for multi-tab support
    if ('BroadcastChannel' in window) {
      this.tabCoordinator = new TabCoordinator('sse-notifications');

      // Listen for leader changes (callback pattern - Vue 2/3 compatible)
      this.tabCoordinator.onLeaderChange((isLeader) => {
        this.isLeaderTab = isLeader;

        // Leader should connect, followers should disconnect
        if (isLeader && this.isAuthenticated() && !this.isConnected) {
          // Add small delay to avoid race conditions during rapid tab switching
          setTimeout(() => {
            // Double-check we're still the leader after the delay
            if (this.isLeaderTab) {
              this.connect();
            }
          }, 100);
        } else if (!isLeader && this.isConnected) {
          // Preserve reconnect state when losing leadership
          this.disconnect(true);
        }
      });

      // Listen for SSE messages from leader tab
      this.tabCoordinator.onMessage((message) => {
        if (message.type === 'SSE_EVENT' && message.eventData) {
          // Reconstruct event-like object for handleNotification
          const syntheticEvent = {
            data: message.eventData
          };
          // console.debug('SSE: Received event from leader tab');
          this.handleNotification(syntheticEvent);
        }
      });
    } else {
      console.warn('SSE: BroadcastChannel not supported, using single-tab mode');
      // Fallback: each tab acts as its own leader
      this.isLeaderTab = true;
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} - Authentication status
   */
  isAuthenticated() {
    if (!this.store) return false;

    const { userDetails } = this.store.state;
    const token = userDetails?.webSessionId || localStorage.getItem('_token');

    return !!(userDetails && Object.keys(userDetails).length > 0 && token);
  }

  /**
   * Get authentication token from store or localStorage
   * @returns {string|null} - Authentication token
   */
  getAuthToken() {
    if (!this.store) return null;

    const { user, userDetails } = this.store.state;
    // Check both possible token locations for consistency with isAuthenticated()
    const token = user?.token || userDetails?.webSessionId || localStorage.getItem('_token');
    return token;
  }

  /**
   * Test SSE endpoint accessibility before connecting
   * @returns {Promise<boolean>} - True if endpoint is accessible, false otherwise
   * @deprecated This function is no longer used as it causes duplicate requests.
   * EventSource handles authentication errors directly.
   */
  // async testEndpointAccessibility() {
  //   // This function has been deprecated to prevent duplicate requests to the SSE endpoint.
  //   // EventSource will handle authentication errors directly without pre-flight checks.
  //   return true;
  // }

  /**
   * Connect to SSE stream if authenticated
   * Only leader tabs establish the actual SSE connection
   */
  async connect() {
    if (this.isConnecting) {
      return;
    }

    if (!this.isAuthenticated()) {
      console.warn('SSE: Cannot connect - user not authenticated');
      return;
    }

    // Only leader tabs should create the EventSource connection
    if (!this.isLeaderTab) {
      return;
    }

    if (this.isConnected) {
      return;
    }

    // Clean up any stale EventSource before creating new one
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    // Don't connect if authentication has already failed
    if (this.authenticationFailed) {
      return;
    }

    this.isConnecting = true;

    try {
      // Add vektor-token as query parameter for session identification
      const token = this.getAuthToken();

      if (!token) {
        console.error('SSE: No authentication token available');
        this.authenticationFailed = true;
        return;
      }

      // Set token as cookie so EventSource can send it (EventSource doesn't support custom headers)
      // Note: This requires backend to accept token from cookies OR query params
      document.cookie = `vektor-token=${encodeURIComponent(token)}; path=/; SameSite=Strict`;

      const streamUrl = `${sse.stream}?vektor-token=${encodeURIComponent(token)}`;

      this.eventSource = new EventSource(streamUrl);

      this.eventSource.addEventListener('open', this.handleOpen.bind(this));
      this.eventSource.addEventListener('SSE-NOTIFICATION', this.handleNotification.bind(this));
      this.eventSource.addEventListener('error', this.handleError.bind(this));

    } catch (error) {
      console.error('SSE: Failed to create EventSource:', error);
      this.scheduleReconnect();
    } finally {
      this.isConnecting = false;
    }
  }


  /**
   * Handle SSE connection open
   */
  handleOpen() {
    this.isConnected = true;
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000; // Reset delay
    this.authenticationFailed = false; // Reset auth failure flag
  }

  /**
   * Handle SSE notification messages
   * Broadcasts to other tabs if leader, then processes locally
   * @param {Event} event - SSE event (or synthetic event from BroadcastChannel)
   */
  handleNotification(event) {
    try {
      // If we're the leader, broadcast this event to all other tabs
      if (this.isLeaderTab && this.tabCoordinator) {
        this.tabCoordinator.broadcastMessage('SSE_EVENT', {
          type: 'SSE_EVENT',
          eventData: event.data
        });
      }

      const message = JSON.parse(event.data);
      const { type } = message;

      if (!this.i18n) {
        console.warn('SSE: i18n not available for notification:', type);
        return;
      }

      const { locale } = this.i18n;
      let text = '';
      if (type === 'SSE-DRIVER-LOCATION-ALERT' || type === 'SSE-DRIVER-ONLINE-ALERT') {
        text = this.i18n.messages[locale]?.flexigo?.notifications[type];

        const distance = Math.round(Number(message.driverDistanceToDepot)) || 0;

        const formattedText = text.replace('{driverName}', message.driverName)
          .replace('{routeName}', message.routeName)
          .replace('{assignmentStartTime}', message.assignmentStartTime)
          .replace('{controlTime}', message.controlTime)
          .replace('{driverDistanceToDepot}', distance);
        text = formattedText;
      } else if (type === 'SSE-DELAYED_TRIP_ALERT') {
        text = this.i18n.messages[locale]?.flexigo?.notifications[type];

        const formattedDepartureHour = timeLocalFormat(message.departureHour);

        const formattedText = text.replace('{departureHour}', formattedDepartureHour)
          .replace('{routeName}', message.routeName)
          .replace('{vehicleName}', message.vehicleName)
          .replace('{driverName}', message.driverName);
        text = formattedText;
      } else {
        text = this.i18n.messages[locale]?.flexigo?.notifications[type];
      }

      // Create navigation data based on notification type
      const navigationData = SSEService.createNavigationData(type, message);

      // Send to service worker for display
      SSEService.sendToServiceWorker(type, locale, text, navigationData);

      // Optionally dispatch to store for app-wide notification handling
      if (this.store && this.store.dispatch) {
        this.store.dispatch('notifications/handleSSENotification', { type, message, text });
      }

    } catch (error) {
      console.error('SSE: Error processing notification:', error);
    }
  }

  /**
   * Create navigation data based on notification type
   * @param {string} type - Notification type
   * @param {Object} message - Original SSE message
   * @returns {Object} Navigation data object
   */
  static createNavigationData(type, message) { // eslint-disable-line
    switch (type) {
      case 'SSE-DRIVER-LOCATION-ALERT':
      case 'SSE-DRIVER-ONLINE-ALERT':
        return {
          routeName: 'FlexiRidePoolAssignments',
          targetUrl: '/flexigo/#/flexiride/pool/assignments'
        };

      case 'SSE-DELAYED_TRIP_ALERT':
        return {
          routeName: 'Dispatch',
          routeParams: { type: 'SHUTTLE' },
          targetUrl: '/flexigo/#/dispatch/SHUTTLE'
        };

      default:
        return {
          routeName: 'notifications',
          targetUrl: '/admin/notifications'
        };
    }
  }

  /**
   * Handle SSE connection errors
   * @param {Event} error - Error event
   */
  handleError(error) {
    console.error('SSE: Connection error:', error);

    // Clear connection flag immediately on error
    this.isConnected = false;

    if (this.eventSource) {
      // EventSource has built-in auto-reconnection that we need to prevent
      // Close the connection immediately to stop browser's automatic reconnection
      if (this.eventSource.readyState === EventSource.CONNECTING ||
          this.eventSource.readyState === EventSource.CLOSED) {
        // After multiple failures, assume authentication issue or persistent network problem
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('SSE: Max reconnection attempts reached, stopping');
          this.authenticationFailed = true;
          // Force close EventSource to prevent browser auto-reconnection
          this.eventSource.close();
          this.eventSource = null;
          this.disconnect(false); // Don't reset counter on final disconnect
          return;
        }

        // Force close to prevent browser's built-in reconnection
        // We'll handle reconnection ourselves with exponential backoff
        this.eventSource.close();
        this.eventSource = null;
        this.scheduleReconnect();
      }
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  scheduleReconnect() {
    // Don't reconnect if authentication has failed
    if (this.authenticationFailed) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('SSE: Max reconnection attempts reached');
      return;
    }

    if (!this.isAuthenticated()) {
      return;
    }

    this.reconnectAttempts++; // eslint-disable-line
    const delay = Math.min(this.reconnectDelay * (2 ** (this.reconnectAttempts - 1)), this.maxReconnectDelay);

    this.reconnectTimeoutId = setTimeout(async () => {
      this.disconnect(true); // Pass true to indicate this is a reconnection
      await this.connect();
    }, delay);
  }

  /**
   * Send notification to service worker for display
   * @param {string} type - Notification type
   * @param {string} locale - Current locale
   * @param {string} text - Notification text
   * @param {Object} navigationData - Navigation data
   */
  static async sendToServiceWorker(type, locale, text, navigationData) {
    if (!('serviceWorker' in navigator)) {
      console.error('SSE: Service Worker not supported');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration('/admin/');

      const messageData = {
        type: 'SHOW_NOTIFICATION',
        data: { type, locale, text, navigationData }
      };

      // Try service worker controller first (preferred method)
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(messageData);
        return;
      }

      // If we have an active service worker, use it directly
      if (registration?.active) {
        registration.active.postMessage(messageData);
        return;
      }

      // If no registration found, try to get ready registration
      if (!registration) {
        try {
          const readyRegistration = await Promise.race([
            navigator.serviceWorker.ready,
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 5000))
          ]);

          if (readyRegistration?.active) {
            readyRegistration.active.postMessage(messageData);
            return;
          }
        } catch (readyError) {
          console.error('SSE: Error waiting for ready registration:', readyError);
        }
      }

      console.error('SSE: No service worker controller available and no active service worker found');
      // Fallback to direct browser notification
      if (Notification.permission === 'granted') {
        // Create notification but don't store reference to avoid memory leaks
        // Browser will handle cleanup automatically
        new Notification('Flexigo Notification', { // eslint-disable-line
          body: text,
          icon: '/admin/flexigo-loader.gif'
        });
      }
    } catch (error) {
      console.error('SSE: Error sending to service worker:', error);
    }
  }

  /**
   * Disconnect from SSE stream
   * Only cleanup TabCoordinator if user is logged out (permanent disconnect)
   * @param {boolean} isReconnecting - If true, don't reset reconnectAttempts
   */
  disconnect(isReconnecting = false) {
    // Clear any pending reconnection timeout
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.isConnected = false;
    // Only reset counter if not part of a reconnection attempt
    if (!isReconnecting) {
      this.reconnectAttempts = 0;
    }

    // Only cleanup TabCoordinator on logout (not on temporary disconnects/reconnects)
    if (this.tabCoordinator && !this.isAuthenticated()) {
      this.tabCoordinator.cleanup();
      this.tabCoordinator = null;
      this.isLeaderTab = false;
    }
  }

  /**
   * Handle authentication state changes
   * @param {boolean} isAuthenticated - New authentication state
   */
  onAuthStateChange(isAuthenticated) {
    if (isAuthenticated) {
      // Reset authentication failure flag when user logs in
      this.authenticationFailed = false;

      // Ensure TabCoordinator is running (it might have been cleaned up on logout)
      this.setupTabCoordinator();

      if (!this.isConnected) {
        this.connect();
      }
    } else if (!isAuthenticated && this.isConnected) {
      this.disconnect();
    }
  }

  /**
   * Get connection status
   * @returns {Object} - Connection status info (Vue 2/3 compatible)
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      readyState: this.eventSource?.readyState,
      reconnectAttempts: this.reconnectAttempts,
      isAuthenticated: this.isAuthenticated(),
      isLeaderTab: this.isLeaderTab,
      tabId: this.tabCoordinator?.tabId || null,
      broadcastChannelSupported: 'BroadcastChannel' in window
    };
  }
}

// Export both the class and singleton instance
export { SSEService };
export default new SSEService();
